import { graphql } from 'gatsby';
import useMinimumStage from './useMinimumStage';
import useStage from './useStage';

/** Merges products and filters them on  */
export default function useStageProducts(medals?: MedalKey[]) {
  const showProducts = useMinimumStage('SHOW_PRODUCTS');
  const transform = useStageProduct();
  return function transformProducts<Product extends RegisteredProduct>(
    products: Product[] | undefined
  ) {
    if (!showProducts) return undefined;
    if (!products) return undefined;
    return products
      .filter((product) => {
        if (!medals) return true;
        if (product.medaille?.key == null) return false;
        return medals.includes(product.medaille.key);
      })
      .map((product) => transform(product));
  };
}

export function useStageProduct() {
  const stage = useStage();
  switch (stage) {
    case 'HIDE_ALL_PRODUCTS':
      return function transform<P extends RegisteredProduct>(): P {
        throw new Error('Product is hidden');
      };
    case 'SHOW_PRODUCTS':
      return function transform<P extends RegisteredProduct>(
        registeredProduct: P
      ): P {
        return {
          ...registeredProduct,
          ...registeredProduct.product,
          medaille: null,
        };
      };
    case 'SHOW_FINALISTS':
      return function transform<P extends RegisteredProduct>(
        registeredProduct: P
      ): P {
        return {
          ...registeredProduct,
          ...registeredProduct.product,
          medaille:
            registeredProduct.medaille?.key === 'landessieger'
              ? {
                  __typename: 'Bottlebooks_SelectFieldValue',
                  key: 'finalist',
                  value: 'Finalist',
                }
              : registeredProduct.medaille,
        };
      };
    case 'SHOW_WINNERS':
      return function transform<P extends RegisteredProduct>(
        registeredProduct: P
      ): P {
        return {
          ...registeredProduct,
          ...registeredProduct.product,
        };
      };
    default:
      exhaustiveCheck(stage);
  }
}

function exhaustiveCheck(value: never): never {
  throw new Error(`Unhandled value: ${value}`);
}

export interface RegisteredProductsQuery {
  bottlebooks: {
    collection?: {
      registeredProducts: {
        nodes: RegisteredProduct[];
      };
    };
  };
}

export type MedalKey = 'finalist' | 'landessieger';

export interface RegisteredProduct {
  medaille: { key: MedalKey | null } | null;
  product?: {};
}

export const fragment = graphql`
  fragment BestOfBurgenland_StageProduct on Bottlebooks_RegisteredProduct {
    medaille: customFieldValue(key: "medaille") {
      __typename
      ... on Bottlebooks_SelectFieldValue {
        key
      }
    }
    product {
      productId
    }
  }
`;
